import React from "react";
import { Lock, Check, PenLine } from "lucide-react";
import { SIGNER_META, FIELD_LABEL, useAuthBlob } from "@/components/contracts/contractUtils";

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function SignedImage({ contractId, fieldId }) {
  const src = useAuthBlob(`/contracts/${contractId}/fields/${fieldId}/image`);
  return src ? <img src={src} alt="signature" className="w-full h-full object-contain object-left-bottom" /> : null;
}

function FilledValue({ f, contractId }) {
  if (f.type === "signature" || f.type === "initials") return <SignedImage contractId={contractId} fieldId={f.id} />;
  if (f.type === "checkbox") return f.value ? <Check className="w-full h-full text-[#0a1a4a]" strokeWidth={3} /> : null;
  return <span className="text-[#0a1a4a] text-[clamp(8px,1.1vw,13px)] leading-none truncate">{f.value}</span>;
}

function SignInput({ f, sign }) {
  const v = sign.values[f.id];
  if (f.type === "signature" || f.type === "initials") {
    const img = f.type === "signature" ? sign.sigUrl : sign.iniUrl;
    return img
      ? <img src={img} alt="" className="w-full h-full object-contain object-left-bottom cursor-pointer" onClick={() => sign.openModal(f.type)} />
      : <button onClick={() => sign.openModal(f.type)} className="w-full h-full flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black/70" data-testid={`sign-field-${f.id}`}><PenLine size={11} /> {f.type === "signature" ? "Sign" : "Initial"}</button>;
  }
  if (f.type === "checkbox") {
    return <button onClick={() => sign.setValue(f.id, !v)} className="w-full h-full flex items-center justify-center" data-testid={`sign-field-${f.id}`}>{v && <Check className="w-full h-full text-[#0a1a4a]" strokeWidth={3} />}</button>;
  }
  if (f.type === "date") return <span className="text-[#0a1a4a] text-[clamp(8px,1.1vw,13px)] leading-none px-0.5" data-testid={`sign-field-${f.id}`}>{sign.today}</span>;
  return <input value={v || ""} onChange={(e) => sign.setValue(f.id, e.target.value)} placeholder={FIELD_LABEL[f.type]} className="!bg-transparent !border-0 !p-0.5 w-full h-full text-[#0a1a4a] text-[clamp(8px,1.1vw,13px)] placeholder:text-black/40 focus:!ring-0" data-testid={`sign-field-${f.id}`} />;
}

export default function FieldBox({ f, page, containerRef, mode, selected, locked, onSelect, onMove, sign, contractId }) {
  const meta = SIGNER_META[f.signer] || SIGNER_META.host;
  const style = { left: `${(f.x / page.w) * 100}%`, top: `${(f.y / page.h) * 100}%`, width: `${(f.w / page.w) * 100}%`, height: `${(f.h / page.h) * 100}%` };
  const mine = mode === "sign" && sign?.mine(f);
  const filled = f.value !== null && f.value !== undefined && f.value !== false && f.value !== "";

  const drag = (e, kind) => {
    if (mode !== "edit" || locked) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect(f.id);
    const r = containerRef.current.getBoundingClientRect();
    const s = page.w / r.width;
    const sx = e.clientX, sy = e.clientY, o = { ...f };
    const move = (ev) => {
      const dx = (ev.clientX - sx) * s, dy = (ev.clientY - sy) * s;
      onMove(f.id, kind === "move"
        ? { x: clamp(o.x + dx, 0, page.w - o.w), y: clamp(o.y + dy, 0, page.h - o.h) }
        : { w: clamp(o.w + dx, 8, page.w - o.x), h: clamp(o.h + dy, 8, page.h - o.y) });
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  if (mode === "edit") {
    return (
      <div style={{ ...style, borderColor: meta.color, background: `${meta.color}26` }} onPointerDown={(e) => drag(e, "move")} onClick={(e) => e.stopPropagation()}
        className={`absolute border-2 ${locked ? "cursor-not-allowed opacity-70" : "cursor-move"} ${selected ? "ring-2 ring-offset-1 ring-black" : ""}`} data-testid={`field-${f.id}`}>
        <span className="absolute -top-4 left-0 whitespace-nowrap text-[9px] font-bold px-1 text-white" style={{ background: meta.color }}>
          {locked && <Lock size={8} className="inline mr-0.5" />}{meta.label} · {FIELD_LABEL[f.type]}{f.required ? " *" : ""}
        </span>
        {!locked && <span onPointerDown={(e) => drag(e, "resize")} className="absolute -right-1 -bottom-1 w-2.5 h-2.5 cursor-se-resize" style={{ background: meta.color }} data-testid={`field-resize-${f.id}`} />}
      </div>
    );
  }
  if (filled) {
    return <div style={style} className="absolute flex items-end overflow-hidden" data-testid={`field-${f.id}`}><FilledValue f={f} contractId={contractId} /></div>;
  }
  if (mine) {
    return (
      <div style={{ ...style, borderColor: meta.color, background: `${meta.color}33` }} className="absolute border-2 border-dashed flex items-end overflow-hidden animate-pulse-slow" data-testid={`field-${f.id}`}>
        <SignInput f={f} sign={sign} />
        {f.required && <span className="absolute -top-3 -right-1 text-[10px] font-bold" style={{ color: meta.color }}>*</span>}
      </div>
    );
  }
  return (
    <div style={{ ...style, borderColor: `${meta.color}88` }} className="absolute border border-dashed flex items-center justify-center" data-testid={`field-${f.id}`}>
      {f.w >= 40 && <span className="text-[8px] font-bold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>}
    </div>
  );
}
